import { useQuery } from '@tanstack/react-query';
import type { Database } from '@pirouette/db';
import { supabase } from '@/lib/supabase';

// Integrations scaffold data layer (Phase 7).
//
// Mirrors @/data/bi.ts and @/data/staff.ts: thin, typed react-query wrappers
// over `supabase.from(...)`. Every row type is derived from the generated
// `@pirouette/db` Database types so the UI tracks the database contract.
// Nothing here bypasses RLS — each call runs as the authenticated admin and the
// per-table policies scope it to the caller's studio (tenant).
//
// THIS IS A SCAFFOLD: connections and webhooks are modeled in the database, but
// no provider behaviour is wired up. There are deliberately NO mutations here —
// Zapier/QuickBooks sync and webhook dispatch are gated behind a connected
// provider and an enabled dispatch worker, neither of which exists yet. These
// are read-only views over (currently empty) tables.

type Row<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row'];

export type IntegrationConnectionRow = Row<'integration_connections'>;
export type WebhookEndpointRow = Row<'webhook_endpoints'>;
export type WebhookDeliveryRow = Row<'webhook_deliveries'>;

// ---------------------------------------------------------------------------
// Queries (read-only)
// ---------------------------------------------------------------------------

/** All integration connections for the studio (one row per provider). */
export function useIntegrationConnections() {
  return useQuery({
    queryKey: ['integrations', 'connections'],
    queryFn: async (): Promise<IntegrationConnectionRow[]> => {
      const { data, error } = await supabase
        .from('integration_connections')
        .select('id, studio_id, provider, status, config, created_at, updated_at')
        .order('provider', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** All webhook endpoints for the studio (most recent first). */
export function useWebhookEndpoints() {
  return useQuery({
    queryKey: ['integrations', 'webhook-endpoints'],
    queryFn: async (): Promise<WebhookEndpointRow[]> => {
      const { data, error } = await supabase
        .from('webhook_endpoints')
        .select('id, studio_id, url, events, active, created_at, updated_at')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Recent webhook delivery attempts for the studio (most recent first). */
export function useWebhookDeliveries(limit = 50) {
  return useQuery({
    queryKey: ['integrations', 'webhook-deliveries', limit],
    queryFn: async (): Promise<WebhookDeliveryRow[]> => {
      const { data, error } = await supabase
        .from('webhook_deliveries')
        .select(
          'id, studio_id, endpoint_id, event, payload, status, attempts, last_attempt_at, created_at',
        )
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
  });
}
